import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person, Phone } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

const MAROON = '#7b0011';
const GOLD = '#c89428';
const CREAM = '#fdf3e4';
const PENDING_MEMBERSHIP_ID_KEY = 'maandvo_pending_membership_id';
const OPEN_COMPLETE_PROFILE_ONCE_KEY = 'maandvo_open_complete_profile_once';

interface RegistrationFieldConfig {
  field_key: string;
  label: string;
  input_type: string;
  placeholder?: string;
  helper_text?: string;
  options: string[];
  is_core: boolean;
  is_enabled: boolean;
  is_required: boolean;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticateWithTokens } = useAuthStore();
  const pendingMembershipId = sessionStorage.getItem(PENDING_MEMBERSHIP_ID_KEY) || '';
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    gender: '',
    phone: '',
    date_of_birth: '',
  });
  const [fieldConfigs, setFieldConfigs] = useState<RegistrationFieldConfig[]>([]);
  const [extraFields, setExtraFields] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [membershipId, setMembershipId] = useState(pendingMembershipId);
  const [membershipDialogOpen, setMembershipDialogOpen] = useState(Boolean(pendingMembershipId));

  useEffect(() => {
    api.get<{ items: RegistrationFieldConfig[] }>('/users/public/registration-fields')
      .then((response) => setFieldConfigs(response.data.items || []))
      .catch(() => toast.error('Failed to load registration form settings'));
  }, []);

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const dynamicFields = fieldConfigs.filter((field) => !field.is_core && field.is_enabled);
  const genderOptions = fieldConfigs.find((field) => field.field_key === 'gender')?.options?.filter(Boolean) || ['MALE', 'FEMALE', 'OTHER'];
  const updateExtraField = (fieldKey: string, value: string) => {
    setExtraFields((previous) => ({ ...previous, [fieldKey]: value }));
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password || !formData.phone || !formData.date_of_birth || !formData.gender) {
      toast.error('Please complete all required fields');
      return;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.date_of_birth)) {
      toast.error('Please enter a valid date of birth');
      return;
    }
    const dateOfBirth = new Date(`${formData.date_of_birth}T00:00:00`);
    const today = new Date();
    const earliestDate = new Date('1900-01-01T00:00:00');
    if (Number.isNaN(dateOfBirth.getTime()) || dateOfBirth > today || dateOfBirth < earliestDate) {
      toast.error('Please enter a valid date of birth');
      return;
    }

    const missingDynamicField = dynamicFields.find((field) => field.is_required && !String(extraFields[field.field_key] || '').trim());
    if (missingDynamicField) {
      toast.error(`${missingDynamicField.label} is required`);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/auth/register', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        gender: formData.gender,
        phone: formData.phone,
        // DOB is a calendar date; do not convert it through UTC.
        date_of_birth: `${formData.date_of_birth}T00:00:00`,
        extra_fields: extraFields,
      });

      // TEMPORARY: Email OTP verification is bypassed.
      // toast.success('Registration successful. Check your email for the verification code.');
      // navigate(`/verify-email?email=${encodeURIComponent(response.data.email || formData.email)}`, { replace: true, state: { email: response.data.email || formData.email } });
      const registeredMembershipId = response.data.profile_id || 'Pending';
      sessionStorage.setItem(PENDING_MEMBERSHIP_ID_KEY, registeredMembershipId);
      setMembershipId(registeredMembershipId);
      setMembershipDialogOpen(true);
      await authenticateWithTokens(response.data.access_token, response.data.refresh_token);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMembershipDialogConfirm = () => {
    sessionStorage.removeItem(PENDING_MEMBERSHIP_ID_KEY);
    sessionStorage.setItem(OPEN_COMPLETE_PROFILE_ONCE_KEY, '1');
    setMembershipDialogOpen(false);
    navigate('/my-profile', { replace: true, state: { openCompleteProfile: true } });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: CREAM,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/maandvo-hero-banner.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.15,
          zIndex: 0,
        }}
      />

      {/* Floral decorations */}
      <Box
        component="img"
        src="/maandvo-floral-left.png"
        sx={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          height: '400px',
          opacity: 0.4,
          zIndex: 1,
          display: { xs: 'none', md: 'block' },
        }}
      />
      <Box
        component="img"
        src="/maandvo-floral-right.png"
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          height: '350px',
          opacity: 0.4,
          zIndex: 1,
          display: { xs: 'none', md: 'block' },
        }}
      />

      {/* Content */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, md: 4 }, minHeight: '90vh' }}>
          {/* Left Section - Desktop Only */}
          <Box
            sx={{
              flex: 1,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            {/* Logo */}
            <Box>
              <Box component="img" src="/maandvo-logo.png" sx={{ height: 80, marginBottom: 2 }} />
              <Typography
                variant="h3"
                sx={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  fontWeight: 800,
                  color: MAROON,
                  marginBottom: 1,
                }}
              >
                MAANDVO
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  color: MAROON,
                  fontWeight: 500,
                  marginBottom: 2,
                }}
              >
                મેટ્રીમોની સર્વિસ
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  color: MAROON,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  marginBottom: 3,
                  lineHeight: 1.6,
                }}
              >
                સારા સંબંધોની સુંદર શરૂઆત<br />વિશ્વાસ, સચ્ચાર અને સમર્પણ સાથે
              </Typography>
            </Box>

            {/* Trust Icons */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 28, marginBottom: 1 }}>🛡️</Box>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 600, display: 'block' }}>
                  સુરક્ષિત પ્રોફાઇલ્સ
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 28, marginBottom: 1 }}>👥</Box>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 600, display: 'block' }}>
                  વ્યક્તિગત સેવા
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 28, marginBottom: 1 }}>🔒</Box>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 600, display: 'block' }}>
                  સુરક્ષિત અને ગોપનીય
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ fontSize: 28, marginBottom: 1 }}>❤️</Box>
                <Typography variant="caption" sx={{ color: MAROON, fontWeight: 600, display: 'block' }}>
                  વિશ્વસનીય
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Section - Register Form */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Card
              sx={{
                width: '100%',
                maxWidth: 520,
                padding: { xs: 3, md: 4 },
                borderRadius: 2,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                backgroundColor: '#fff',
              }}
            >
              {/* Form Title */}
              <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
                <Typography
                  sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    color: MAROON,
                    fontFamily: "'Georgia', 'Times New Roman', serif",
                    marginBottom: 1,
                  }}
                >
                  Candidate Registration Form
                </Typography>
                <Typography variant="body2" sx={{ color: '#8a7a6d', fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                  Create your new account
                </Typography>
              </Box>

              <form onSubmit={handleRegister}>
                {/* First Name */}
                <TextField
                  fullWidth
                  label="Candidate First Name"
                  placeholder="Enter your first name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: MAROON }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      '& fieldset': { borderColor: '#e8d4b0' },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: MAROON },
                    },
                  }}
                />

                {/* Last Name */}
                <TextField
                  fullWidth
                  label="Candidate Last Name"
                  placeholder="Enter your last name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      '& fieldset': { borderColor: '#e8d4b0' },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: MAROON },
                    },
                  }}
                />

                {/* Email */}
                <TextField
                  fullWidth
                  label="Email"
                  placeholder="Enter your email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: MAROON }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      '& fieldset': { borderColor: '#e8d4b0' },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: MAROON },
                    },
                  }}
                />

                {/* Gender */}
                <TextField
                  fullWidth
                  select
                  label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={(e: any) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                    margin="normal"
                    sx={{
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e8d4b0' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: MAROON },
                    }}
                >
                    {genderOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                </TextField>

                {dynamicFields.map((field) => (
                  <TextField
                    key={field.field_key}
                    fullWidth
                    select={field.input_type === 'select'}
                    required={field.is_required}
                    label={field.label}
                    placeholder={field.placeholder || undefined}
                    type={field.input_type === 'email' || field.input_type === 'date' || field.input_type === 'password' ? field.input_type : 'text'}
                    value={extraFields[field.field_key] || ''}
                    onChange={(event) => updateExtraField(field.field_key, event.target.value)}
                    helperText={field.helper_text || undefined}
                    margin="normal"
                  >
                    {field.input_type === 'select' && field.options.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </TextField>
                ))}

                <TextField
                  fullWidth
                  required
                  label="Mobile Number"
                  placeholder="Example: 9876543210"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  margin="normal"
                  InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: MAROON }} /></InputAdornment> }}
                />

                <TextField
                  fullWidth
                  required
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  margin="normal"
                  inputProps={{ min: '1900-01-01', max: new Date().toISOString().slice(0, 10) }}
                  InputLabelProps={{ shrink: true }}
                />

                {/* Password */}
                <TextField
                  fullWidth
                  label="Password"
                  placeholder="Enter your password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: MAROON }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: MAROON }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      '& fieldset': { borderColor: '#e8d4b0' },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: MAROON },
                    },
                  }}
                />

                {/* Confirm Password */}
                <TextField
                  fullWidth
                  label="Confirm Password"
                  placeholder="Enter your password again"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: MAROON }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: MAROON }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      '& fieldset': { borderColor: '#e8d4b0' },
                      '&:hover fieldset': { borderColor: GOLD },
                      '&.Mui-focused fieldset': { borderColor: MAROON },
                    },
                  }}
                />

                {/* Register Button */}
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: MAROON,
                    color: '#fff9f2',
                    padding: '12px',
                    fontSize: '1rem',
                    fontFamily: "'Georgia', 'Times New Roman', serif",
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    marginTop: 2,
                    marginBottom: 2,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: '#5d000d' },
                  }}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#fff9f2' }} /> : 'Create Account'}
                </Button>

                {/* Login Link */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#8a7a6d', fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                    Already have an account?{' '}
                    <Link
                      onClick={() => navigate('/login')}
                      sx={{
                        color: MAROON,
                        fontWeight: 700,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Sign In
                    </Link>
                  </Typography>
                </Box>
              </form>
            </Card>
          </Box>
        </Box>
      </Container>

      <Dialog
        open={membershipDialogOpen}
        onClose={() => undefined}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle
          sx={{
            color: MAROON,
            textAlign: 'center',
            fontFamily: "'Georgia', 'Times New Roman', serif",
            fontWeight: 800,
            fontSize: { xs: '1.45rem', sm: '1.8rem' },
          }}
        >
          Registration Successful
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', px: { xs: 2, sm: 4 }, py: 2 }}>
          <Typography sx={{ color: '#4f3b32', fontSize: { xs: '1.05rem', sm: '1.2rem' }, lineHeight: 1.7 }}>
            Your Membership ID is
          </Typography>
          <Typography sx={{ color: MAROON, fontWeight: 900, fontSize: { xs: '1.8rem', sm: '2.3rem' }, my: 1.5 }}>
            {membershipId}
          </Typography>
          <Typography sx={{ color: '#4f3b32', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
            Please take a screenshot for future reference.
          </Typography>
          <Typography lang="gu" sx={{ color: '#4f3b32', mt: 2, fontSize: '1rem', lineHeight: 1.8, overflowWrap: 'anywhere' }}>
            કૃપા કરીને 2 દિવસની અંદર Profile ની Complete માહિતી અને જરૂરી Payment પૂર્ણ કરી આપશો. અન્યથા તમારું Account automatically Delete થઈ જશે.કોઈ મદદ માટે 97 2525 2323 પર call/ WhatsApp કરશો.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={handleMembershipDialogConfirm}
            variant="contained"
            sx={{ backgroundColor: MAROON, px: 5, py: 1.2, fontWeight: 800, '&:hover': { backgroundColor: '#5d000d' } }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer Notice */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: '#fff9f2',
          padding: 2,
          textAlign: 'center',
          fontSize: '0.85rem',
          zIndex: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: '#fff9f2' }}>
          Your information is securely protected.
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterPage;
